// RecipeFlipCard — visual flip wrapper. Tap the toggle button (a
// small chip at the bottom of the front side) to rotate the card
// 180° on Y. Front = the existing RecipePage (recipe content).
// Back = RecipeCookbookSide (the "cookbook" entry for this slug:
// cook count, last cooked, photos, reviews).
//
// Why a wrapper and not in-place edits to RecipeBook.RecipePage?
// RecipeBook.jsx is at 2,129 lines — past the 1,500 line tripwire
// in CLAUDE.md. Adding the flip + back-side inline would push it
// further. A wrapper keeps the change small + the new behavior
// isolated for testing.

import { useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import RecipeCookbookSide from "./RecipeCookbookSide";

export default function RecipeFlipCard({
  recipe,
  userId,
  children,           // the front side — the existing RecipePage
}) {
  const [flipped, setFlipped] = useState(false);
  const slug = recipe?.slug;

  // iOS Safari frequently fails `backface-visibility: hidden` when
  // the rotating element is inside a backdrop-filter stacking
  // context — both faces render simultaneously, the back's
  // mirrored text bleeds through. Belt-and-suspenders: opacity +
  // visibility delay so only ONE face is ever shown, no matter
  // what backface-visibility does.

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        perspective: 1600,
        isolation: "isolate",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          WebkitTransformStyle: "preserve-3d",
          transition: "transform 600ms cubic-bezier(0.2, 0.7, 0.2, 1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          willChange: "transform",
        }}
      >
        {/* Front face */}
        <div
          aria-hidden={flipped}
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            display: "flex",
            flexDirection: "column",
            // Belt-and-suspenders: hard hide when flipped so the
            // mirrored content can't bleed through on iOS.
            opacity: flipped ? 0 : 1,
            visibility: flipped ? "hidden" : "visible",
            transition: "opacity 200ms linear 300ms, visibility 0s linear 300ms",
            pointerEvents: flipped ? "none" : "auto",
          }}
        >
          {children}
          <FlipTab
            label="Cooks + notes"
            emoji="📖"
            onClick={() => setFlipped(true)}
          />
        </div>

        {/* Back face */}
        <div
          aria-hidden={!flipped}
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            display: "flex",
            flexDirection: "column",
            background: COLOR.deep,
            overflow: "hidden",
            opacity: flipped ? 1 : 0,
            visibility: flipped ? "visible" : "hidden",
            transition: "opacity 200ms linear 300ms, visibility 0s linear 300ms",
            pointerEvents: flipped ? "auto" : "none",
          }}
        >
          <RecipeCookbookSide recipe={recipe} userId={userId} />
          <FlipTab
            label="Back to recipe"
            emoji="🔄"
            onClick={() => setFlipped(false)}
          />
        </div>
      </div>
    </div>
  );
}

function FlipTab({ label, emoji, onClick }) {
  // Small corner button — was a wide bottom-center pill that covered
  // content underneath. Now sits in the top-right with just an icon
  // + minimal label. Title is the aria-label for screen readers.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="mcm-focusable"
      style={{
        position: "absolute",
        top: "calc(14px + env(safe-area-inset-top))",
        right: 14,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        padding: 0,
        background: "rgba(255,255,255,0.94)",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 999,
        fontSize: 18,
        lineHeight: 1,
        cursor: "pointer",
        boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
        touchAction: "manipulation",
        zIndex: 50,
      }}
    >
      {emoji}
    </button>
  );
}
